/**
 * I18nField — one inline control for the *current display language* plus a
 * ``🌐 <LANG>`` button that opens a popup to edit every supported locale.
 *
 * Replaces the old "two side-by-side inputs (CS + EN)" pattern that was
 * hand-rolled across ~dozen admin forms. Promotes the look of the materials
 * ``NameField`` (Plast/Kov category matrix) into a single reusable primitive,
 * with two upgrades:
 *
 *   1. The inline control follows ``i18n.language`` — switch the app language
 *      and the field (and the button badge) reflow to that locale. The old
 *      NameField always showed Czech.
 *   2. It works in both styling worlds: the Tailwind admin forms (default
 *      ``inputClassName`` mirrors ``LabeledInput``) and the inline-styled
 *      materials/platform modals (pass ``inputStyle`` + ``inputClassName=""``,
 *      or ``inputClassName=""`` alone to defer to an ambient ``[&_input]``
 *      wrapper).
 *
 * The chrome (button + popup) is inline-styled with the app's design tokens
 * (``var(--ink)`` …) so it looks identical regardless of which world hosts it.
 * The popup is body-portalled + centered, immune to ancestor overflow /
 * sticky-row stacking traps. Edits are live; ``onBlur`` (optional) fires on
 * control blur and on popup close for blur-commit forms.
 */

import {
  useEffect,
  useId,
  useState,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { DEFAULT_LANG } from "@/i18n";
import { getActiveLangCodes } from "@/i18n/languageRegistry";
import { Icon } from "@/pages/admin/platformProcessesIcons";

// Keyed by language code (``cs`` / ``en`` / any admin-added locale). Widened
// from the old ``{cs, en}`` union so a custom language can carry authored text
// (blob-shaped fields; the any-locale read fallback keeps single-language
// records rendering). Base+companion columns stay two-language until a third
// language is real.
export type I18nValue = Record<string, string | undefined>;

const DEFAULT_INPUT_CLASS =
  "block h-10 w-full rounded-md border border-border-strong bg-surface px-3 text-sm text-ink hover:border-ink-3 focus:outline-none";
const DEFAULT_TEXTAREA_CLASS =
  "block w-full rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-ink hover:border-ink-3 focus:outline-none";

/** Reserve room on the right edge for the language button. */
const BUTTON_GUTTER = 46;

interface I18nFieldProps {
  value: I18nValue;
  onChange: (next: I18nValue) => void;
  /** Label rendered above the control (string or styled node). */
  label?: ReactNode;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  testId?: string;
  help?: ReactNode;
  /** Override inline-control classes (default mirrors LabeledInput). "" defers to ambient CSS. */
  inputClassName?: string;
  /** Inline style for hosts that style inputs in JS (materials/platform world). */
  inputStyle?: CSSProperties;
  /** Outer container className / style — e.g. ``flex-1`` inside a row. */
  className?: string;
  style?: CSSProperties;
  /** Blur-commit callback: fired on control blur and popup close. */
  onBlur?: () => void;
  /**
   * Locales the editor offers, in order. Defaults to the two builtin
   * languages ({@link DEFAULT_EDIT_LOCALES}) because every current write path
   * persists a ``_cs`` / ``_en`` pair — a base+companion column can't store a
   * third language, so showing a custom-locale input there would silently
   * drop it on save. A field backed by a genuine N-language store passes
   * ``locales={getActiveLangCodes()}`` to opt into the full enabled set.
   * Any locale already present in ``value`` is always shown too (so existing
   * data stays editable / clearable even when not in this list).
   */
  locales?: string[];
}

/**
 * Default editor locales: the two builtin languages. The whole admin write
 * layer is two-language (``display_name_cs`` / ``display_name_en`` etc.), so
 * this — not the runtime registry — is the safe default. Custom-language
 * *UI-string* translation is handled by the platform "Jazyky a překlady"
 * page (overrides + bundles), which writes to a real N-language store.
 */
export const DEFAULT_EDIT_LOCALES: readonly string[] = [DEFAULT_LANG, "en"];

/** Map an i18next language tag (``cs``, ``en-US``) to an enabled locale code. */
export function resolveActiveLang(lang: string | undefined): string {
  if (!lang) return DEFAULT_LANG;
  return (
    getActiveLangCodes().find((l) => lang === l || lang.startsWith(`${l}-`)) ??
    DEFAULT_LANG
  );
}

/**
 * Single source of truth for the *read/display* fallback rule: resolve an
 * {@link I18nValue} blob to the string to show for ``lang``. When the requested
 * locale is blank we fall back to the base/default locale, then to any other
 * authored locale — so a record authored in Czech only still renders its Czech
 * text under the English UI instead of going blank. Empty string means *no*
 * locale carries any text.
 *
 * Every customer-facing and admin-preview render of an I18nField-authored value
 * must go through this (or {@link pickI18nText}) — never re-implement
 * ``lang === "en" && en ? en : base`` inline.
 */
export function resolveI18nText(
  value: I18nValue | null | undefined,
  lang: string | undefined,
): string {
  if (!value) return "";
  const active = resolveActiveLang(lang);
  for (const l of [active, DEFAULT_LANG, ...getActiveLangCodes()]) {
    const v = value[l];
    if (v) return v;
  }
  // Last resort: any authored locale (incl. a now-disabled one) so the text
  // never blanks. Mirrors the backend ``resolve_i18n_text`` any-locale fallback.
  for (const v of Object.values(value)) {
    if (v && v.trim()) return v;
  }
  return "";
}

/**
 * Convenience over {@link resolveI18nText} for the backend response shape that
 * carries the base column plus a nullable ``*_en`` companion (e.g.
 * ``carrier.name`` + ``carrier.name_en``). Folds the two strings into the
 * canonical ``{cs, en}`` blob and applies the same fallback.
 */
export function pickI18nText(
  base: string | null | undefined,
  en: string | null | undefined,
  lang: string | undefined,
): string {
  return resolveI18nText({ [DEFAULT_LANG]: base ?? "", en: en ?? "" }, lang);
}

/**
 * Totéž jako {@link pickI18nText}, ale pro odpověď API, která nese překlady
 * per jazyk platformy (``name_i18n``) vedle starého páru ``name`` +
 * ``name_en`` (KAN-1804). Neprázdný blob vyhrává; chybí-li (starší odpověď,
 * řádek, který nikdo nepřevedl), složí se z páru. Fallback při zobrazení je
 * tentýž jako všude: jazyk → výchozí → cokoli.
 */
export function pickI18nBlob(
  blob: I18nValue | null | undefined,
  base: string | null | undefined,
  en: string | null | undefined,
  lang: string | undefined,
): string {
  if (blob && !isI18nValueEmpty(blob)) return resolveI18nText(blob, lang);
  return pickI18nText(base, en, lang);
}

/**
 * Hodnota pro {@link I18nField} z odpovědi API: neprázdný blob per jazyk
 * platformy, jinak starý pár ``base`` + ``en`` (KAN-1804).
 */
export function i18nValueOf(
  blob: I18nValue | null | undefined,
  base: string | null | undefined,
  en: string | null | undefined,
): I18nValue {
  if (blob && !isI18nValueEmpty(blob)) return { ...blob };
  return { [DEFAULT_LANG]: base ?? "", en: en ?? "" };
}

/** Stejné texty bez ohledu na prázdné jazyky a pořadí klíčů (JSONB je přeřadí). */
export function sameI18nTexts(a: I18nValue, b: I18nValue): boolean {
  const x = i18nValueToWrite(a);
  const y = i18nValueToWrite(b);
  const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
  return [...keys].every((k) => (x[k] ?? "") === (y[k] ?? ""));
}

/** Blob pro zápis do API: bez prázdných jazyků (server je stejně zahodí). */
export function i18nValueToWrite(value: I18nValue): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [lang, text] of Object.entries(value)) {
    if (text && text.trim()) out[lang] = text;
  }
  return out;
}

/** True when no locale carries any (non-whitespace) text. */
export function isI18nValueEmpty(value: I18nValue | null | undefined): boolean {
  if (!value) return true;
  return !Object.values(value).some((v) => (v ?? "").trim().length > 0);
}

export function I18nField({
  value,
  onChange,
  label,
  required = false,
  multiline = false,
  rows = 3,
  placeholder,
  maxLength,
  disabled = false,
  autoFocus = false,
  testId,
  help,
  inputClassName,
  inputStyle,
  className,
  style,
  onBlur,
  locales,
}: I18nFieldProps): JSX.Element {
  const { t, i18n } = useTranslation("common");
  const editLocales = locales ?? DEFAULT_EDIT_LOCALES;
  // The inline control edits the current display language *if* it is one this
  // field offers; otherwise it falls back to the default so a display locale
  // outside the field's edit set (e.g. a custom UI language on a two-language
  // field) never writes a value the save path would drop.
  const displayLang = resolveActiveLang(i18n.language);
  const lang = editLocales.includes(displayLang) ? displayLang : DEFAULT_LANG;
  const [open, setOpen] = useState(false);
  // KAN-1979: popisek nad polem byl jen <div> — čtečka obrazovky pole
  // nepojmenovala a getByLabelText ho nenašel. aria-labelledby (ne <label>),
  // protože volající občas I18nField sami balí do <label>.
  const labelId = useId();

  const editLabel = t("i18nField.edit", { defaultValue: "Upravit překlady" });
  const current = value[lang] ?? "";
  // Read-fallback for *display*: when the active locale is blank, surface the
  // resolved fallback (base CS, then any other authored locale) as a
  // placeholder hint — so a record authored in Czech only reads as its Czech
  // text under the EN UI instead of going blank. The (empty) active-locale
  // *value* is left untouched, so editing/saving the English copy still works
  // and ``required`` stays driven by isI18nValueEmpty. Mirrors resolveI18nText.
  const fallbackHint = current ? "" : resolveI18nText(value, lang);
  const effectivePlaceholder = fallbackHint || placeholder;
  const setLang = (l: string, next: string): void =>
    onChange({ ...value, [l]: next });
  // ``required`` means "at least one language must be filled", not "this
  // locale's input". Only assert the native HTML constraint while *every*
  // locale is blank — once any locale has text (e.g. base CS), the currently
  // shown (possibly empty) locale must not block the form. See isI18nValueEmpty.
  const requireCurrent = required && isI18nValueEmpty(value);

  const controlClass =
    inputClassName ?? (multiline ? DEFAULT_TEXTAREA_CLASS : DEFAULT_INPUT_CLASS);
  // paddingRight goes *after* the spread so a caller's ``padding`` shorthand
  // (e.g. the materials ``inputStyle()`` world) can't clobber the gutter that
  // keeps the language button from overlapping the text.
  const controlStyle: CSSProperties = {
    ...inputStyle,
    paddingRight: inputStyle?.paddingRight ?? BUTTON_GUTTER,
  };

  const sharedControlProps = {
    value: current,
    placeholder: effectivePlaceholder,
    maxLength,
    disabled,
    autoFocus,
    onBlur,
    "data-testid": testId,
    "aria-labelledby": label != null ? labelId : undefined,
    className: controlClass,
    style: controlStyle,
  };

  return (
    <div className={className} style={style}>
      {label != null && (
        <div className="mb-1 block text-sm text-ink">
          <span id={labelId}>{label}</span>
          {required && <span className="text-danger"> *</span>}
        </div>
      )}
      <div style={{ position: "relative" }}>
        {multiline ? (
          <textarea
            {...sharedControlProps}
            rows={rows}
            onChange={(e) => setLang(lang, e.target.value)}
          />
        ) : (
          <input
            {...sharedControlProps}
            type="text"
            required={requireCurrent}
            onChange={(e) => setLang(lang, e.target.value)}
          />
        )}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={editLabel}
          title={editLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid={testId ? `${testId}-i18n` : undefined}
          style={{
            position: "absolute",
            right: 5,
            top: multiline ? 6 : "50%",
            transform: multiline ? undefined : "translateY(-50%)",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2.5px 7px",
            borderRadius: 5,
            border: `1px solid ${open ? "var(--border-strong)" : "var(--border)"}`,
            background: open ? "var(--surface-3)" : "var(--surface-2)",
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            fontWeight: 600,
            color: "var(--ink-3)",
            cursor: "pointer",
          }}
        >
          <Icon name="globe" size={10} stroke="var(--ink-4)" />
          {lang.toUpperCase()}
        </button>
      </div>
      {help != null && <div className="mt-1 block text-xs text-ink-3">{help}</div>}

      <I18nPopup
        open={open}
        value={value}
        onChange={onChange}
        multiline={multiline}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        title={label}
        testId={testId}
        locales={editLocales}
        onClose={() => {
          setOpen(false);
          onBlur?.();
        }}
      />
    </div>
  );
}

const POPUP_INPUT_STYLE: CSSProperties = {
  flex: 1,
  width: "100%",
  padding: "6px 10px",
  fontSize: 12.5,
  color: "var(--ink)",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  outline: "none",
  boxSizing: "border-box",
};

function I18nPopup({
  open,
  value,
  onChange,
  multiline,
  rows,
  placeholder,
  maxLength,
  title,
  testId,
  locales,
  onClose,
}: {
  open: boolean;
  value: I18nValue;
  onChange: (next: I18nValue) => void;
  multiline: boolean;
  rows: number;
  placeholder?: string;
  maxLength?: number;
  title?: ReactNode;
  testId?: string;
  locales: readonly string[];
  onClose: () => void;
}): JSX.Element | null {
  const { t } = useTranslation("common");

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const closeLabel = t("i18nField.close", { defaultValue: "Zavřít" });
  const setLang = (l: string, next: string): void =>
    onChange({ ...value, [l]: next });
  // The field's declared locales first, then any locale already authored in
  // ``value`` outside that set (e.g. legacy data) so its text stays editable.
  const editCodes = [
    ...locales,
    ...Object.keys(value).filter((k) => !locales.includes(k)),
  ];

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("i18nField.title", { defaultValue: "Překlady" })}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          border: "none",
          padding: 0,
          background: "rgba(12,10,9,0.45)",
          cursor: "default",
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 440,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
          padding: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div className="text-sm font-semibold text-ink">
            {title ?? t("i18nField.title", { defaultValue: "Překlady" })}
          </div>
          <button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              flexShrink: 0,
              borderRadius: 6,
              border: "1px solid var(--border)",
              background: "var(--surface-2)",
              color: "var(--ink-3)",
              fontSize: 12,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {editCodes.map((l) => (
            <label
              key={l}
              style={{
                display: "flex",
                alignItems: multiline ? "flex-start" : "center",
                gap: 9,
              }}
            >
              <span
                className="font-mono"
                style={{
                  width: 26,
                  flexShrink: 0,
                  paddingTop: multiline ? 8 : 0,
                  fontSize: 10,
                  fontWeight: 600,
                  color: "var(--ink-4)",
                }}
              >
                {l.toUpperCase()}
              </span>
              {multiline ? (
                <textarea
                  value={value[l] ?? ""}
                  rows={rows}
                  placeholder={placeholder}
                  maxLength={maxLength}
                  onChange={(e) => setLang(l, e.target.value)}
                  data-testid={testId ? `${testId}-i18n-${l}` : undefined}
                  style={{ ...POPUP_INPUT_STYLE, resize: "vertical" }}
                />
              ) : (
                <input
                  value={value[l] ?? ""}
                  placeholder={placeholder}
                  maxLength={maxLength}
                  onChange={(e) => setLang(l, e.target.value)}
                  data-testid={testId ? `${testId}-i18n-${l}` : undefined}
                  style={POPUP_INPUT_STYLE}
                />
              )}
            </label>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "1px solid var(--ink)",
              background: "var(--ink)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {t("i18nField.done", { defaultValue: "Hotovo" })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
